import PaymentTypes from '@/models/PaymentTypes';
import Vars from '@/config/vars';
import mocked from '@/mockedMerchantSession';
import { logException } from '@/helpers/cieloLogger';
import axios from '@/config/axios.config';

export default {
    namespaced: true,

    state: {
        isApplePayEnabled: false,
        isReadyToPay: false,
        allowedInstallments: 0,

        paymentRequest: {
            countryCode: 'BR',
            currencyCode: 'BRL',
            merchantCapabilities: [
                'supports3DS',
                'supportsCredit'
            ],
            supportedNetworks: [],
            requiredBillingContactFields: [
                'postalAddress',
            ],
            requiredShippingContactFields: [
                'name',
                'email',
                'phone',
            ],
            total: {
                label: '',
                type: 'final',
                amount: '0.00'
            },
        },

        consumer: {
            email: '',
            phone: '',
            fullName: '',
        },
        billingAddress: null,
        selectedNetwork: '',

        paymentMethodToken: {
            Type: 'ApplePay',
            WalletKey: '',
            AdditionalData: {
                EphemeralPublicKey: ''
            }
        },
    },

    getters: {
        getSubmitModel(state, getters, rootState, rootGetters) {
            const submitModel = {
                ...rootGetters['cardPayment/getSubmitModel'],
                PaymentType: PaymentTypes.CREDIT_CARD,
                Guest: {
                    FullName: state.consumer.fullName,
                    Email: state.consumer.email,
                    Phone: state.consumer.phone,
                    Identity: rootState.customer.Identity,
                    IdentityType: rootState.customer.IdentityType,
                    AcceptAds: rootState.customer.AcceptAds,
                    UserAgent: rootState.customer.UserAgent
                },
                Wallet: state.paymentMethodToken,
            };

            return submitModel;
        },

        getAllowedInstallments: state => state.allowedInstallments,
    },

    mutations: {
        setFromRequest(state, data) {
            if (data.WalletMethodInformation && data.SupportedCardBrands) {
                const applePayCredentials = data.WalletMethodInformation.find(item => item.Name === 'Apple Pay') || null;
                const supportedCardBrands = data.SupportedCardBrands.filter(obj => obj.Wallets && obj.Wallets.includes('ApplePay')).map((obj) => {
                    if (obj.Name === 'Mastercard') return 'masterCard';

                    return obj.Name.toLowerCase();
                }) || [];

                if (applePayCredentials && supportedCardBrands.length && applePayCredentials.AllowedInstallments) {
                    state.isApplePayEnabled = true;
                    state.paymentRequest.total.label = data.MerchantName;
                    state.allowedInstallments = applePayCredentials.AllowedInstallments;
                    state.paymentRequest.supportedNetworks = supportedCardBrands.map(brand => brand);
                }
            }

        },

        setFromPaymentData(state, data) {
            //Preencher dados de billing address
            state.billingAddress = data.billingContact;

            //Preencher dados do consumer
            state.consumer.fullName = `${data.shippingContact.givenName} ${data.shippingContact.familyName || data.billingContact.familyName || ''}`.trim();
            state.consumer.email = data.shippingContact.emailAddress;
            state.consumer.phone = data.shippingContact.phoneNumber;

            //Preencher dados de Token
            state.paymentMethodToken.WalletKey = data.token.paymentData.data;
            state.paymentMethodToken.AdditionalData.EphemeralPublicKey = data.token.paymentData.header.ephemeralPublicKey;

            //Preencher cartao selecionado
            state.selectedNetwork = data.token &&
                data.token.paymentMethod &&
                data.token.paymentMethod.network
                ? data.token.paymentMethod.network.toUpperCase()
                : '';
        },

        setTotalPrice(state, totalPrice) {
            state.paymentRequest.total.amount = totalPrice;
        },

        setIsReadyToPay(state, isReadyToPay) {
            state.isReadyToPay = isReadyToPay;
        },
        
        resetBillingAddress(state) {
            state.billingAddress = null;
        }
    },

    actions: {
        setPaymentMethod({ rootGetters, rootState }, paymentMethodId) {
            let paymentMethod = rootGetters['payment/getPaymentMethodById'](paymentMethodId);
            if (!paymentMethod)
                throw new Error('Meio de pagamento não suportado');

            rootState.payment.PaymentMethodId = paymentMethod.Id;
        },

        async fetchCardBrand({ dispatch, commit, rootState, state }) {
            let paymentType = PaymentTypes.CREDIT_CARD;
            let preorderId = rootState.preorder.Id;
            let defaultBins = {
                AMEX: 377467,
                DISCOVER: 601102,
                JCB: 358292,
                VISA: 462294,
                MASTERCARD: 518600,
            };

            let response;

            if (Vars.USE_SIMULATED_MODE)
                response = await new Promise(resolve => setTimeout(() => resolve(buildSimulatedResponse()), 1000));
            else
                response = await axios.get(`brand/${preorderId}/${paymentType}/${defaultBins[state.selectedNetwork]}`);

            let paymentMethodId = response.data.PaymentMethodId;
            let maxNumberOfPayments = 0;

            if (response.data.InstallmentsInfo !== null && response.data.InstallmentsInfo.length > 0)
                maxNumberOfPayments = response.data.InstallmentsInfo.length;

            commit(
                'payment/setPaymentMethodNumberOfInstallments',
                {
                    paymentMethodId,
                    maxNumberOfPayments
                },
                { root: true });
            dispatch('setPaymentMethod', paymentMethodId);
        },

        async validateMerchant({ commit, rootState }, validationURL = 'https://apple-pay-gateway.apple.com/paymentservices/paymentSession') {
            try {
                const obj = {
                    preorderId: rootState.preorder.Id,
                    AppleDynamicUrl: validationURL
                };

                let response;

                if (Vars.USE_SIMULATED_MODE)
                    response = await new Promise(resolve => setTimeout(() => resolve({ data: mocked }), 3000));
                else
                    response = await axios.post(`/apple/session`, obj);

                const data = response.data.Data.session;

                return data;
            } catch (error) {
                logException(error);
                throw new Error('Erro durante a validação do merchant');
            }
        },

        setBillingAddress({ commit, dispatch, rootGetters, state }) {
            dispatch('billingAddress/setFromApplePay', state.billingAddress, { root: true });
        },

        async initApplePaySession({ state, commit, dispatch }) {
            return new Promise((resolve, reject) => {
                const request = state.paymentRequest;
                const session = new ApplePaySession(3, request);

                session.onvalidatemerchant = async event => {

                    const merchantSession = await dispatch('validateMerchant', event.validationURL);

                    if (merchantSession) {
                        session.completeMerchantValidation(merchantSession);
                    } else {
                        reject(new Error('applePaySesssionError'));
                    }
                };

                session.onpaymentauthorized = async event => {
                    const billingContact = event.payment.billingContact;
                    const shippingContact = event.payment.shippingContact;
                    const errors = [];
                    let result = {
                        status: ApplePaySession.STATUS_SUCCESS,
                    };

                    if (
                        !billingContact ||
                        !billingContact.addressLines ||
                        !billingContact.addressLines[0] ||
                        !billingContact.postalCode ||
                        !billingContact.locality ||
                        !billingContact.subLocality ||
                        !billingContact.administrativeArea
                    )  {
                        errors.push(new ApplePayError(
                            'billingContactInvalid',
                            'postalCode',
                            'addressLines is required.'
                        ));

                        session.completePayment({
                            status: ApplePaySession.STATUS_FAILURE,
                            errors: errors,
                        });

                        reject(new Error('applePayBillingContactError'));
                    }

                    if(
                        !shippingContact ||
                        !shippingContact.givenName ||
                        !shippingContact.emailAddress ||
                        !shippingContact.phoneNumber
                    ) {
                        errors.push(new ApplePayError(
                            'shippingContactInvalid',
                            'givenName',
                            'givenName is required.'
                        ));

                        session.completePayment({
                            status: ApplePaySession.STATUS_FAILURE,
                            errors: errors,
                        });

                        reject(new Error('applePayShippingContactError'));
                    }

                    try {
                        commit('setFromPaymentData', event.payment);
                        dispatch('setBillingAddress');
                        await dispatch('fetchCardBrand');
                    } catch (error) {
                        result.status = ApplePaySession.STATUS_FAILURE;
                        reject(new Error('applePayCompletePaymentError'));
                    } finally {
                        session.completePayment(result);
                        resolve();
                    }
                };

                session.oncancel = () => {
                    commit('resetBillingAddress');
                    reject();
                };

                session.begin();
            });
        }
    }
};

function buildSimulatedResponse() {
    return {
        data: {
            Brand:'Master',
            PaymentMethodId: 2,
            Success: true,
            ReasonError: null,
            InstallmentsInfo: [
                { Installments: 1 },
                { Installments: 2 },
                { Installments: 3 },
                { Installments: 4 },
                { Installments: 5 },
                { Installments: 6 },
                { Installments: 7 },
                { Installments: 8 }
            ]
        }
    };
}
